import { Btn, type InputFrame } from '../sim/types.ts';

/**
 * Keyboard -> input bitmask (brief §11).
 *
 * Deliberately dumb: it reports which buttons are held this instant and nothing
 * else. Edge detection, buffering and coyote time all live in the simulation, so
 * that a replayed input frame behaves identically to a live one.
 */
const BINDINGS: Readonly<Record<string, InputFrame>> = {
  ArrowLeft: Btn.Left,
  KeyA: Btn.Left,
  ArrowRight: Btn.Right,
  KeyD: Btn.Right,
  // W jumps and Space casts (set 2026-09-04). With A and D already on move,
  // this puts the whole left hand on WAD and the cast under the thumb.
  // Z, V and X stay bound as alternates; they cost nothing to keep.
  KeyW: Btn.Jump,
  KeyZ: Btn.Jump,
  // Held with cast, this aims the cast underfoot instead of ahead.
  KeyS: Btn.Down,
  ArrowDown: Btn.Down,
  Space: Btn.Cast,
  KeyV: Btn.Cast,
  KeyX: Btn.Cast,
  KeyF: Btn.Ranged,
  KeyL: Btn.Ranged,
  KeyE: Btn.Activate,
};

/**
 * Fallback matched on `event.key` when `event.code` is empty.
 *
 * `code` is the correct primary binding: it is physical-layout independent, so
 * WASD stays where the player's fingers are on AZERTY. But `code` is not always
 * populated — synthetic events, some remote-desktop and virtual-keyboard stacks,
 * and automation deliver an empty string — and a game that silently ignores all
 * input in those cases is worse than one that falls back to the logical key.
 */
const KEY_FALLBACK: Readonly<Record<string, InputFrame>> = {
  arrowleft: Btn.Left,
  a: Btn.Left,
  arrowright: Btn.Right,
  d: Btn.Right,
  w: Btn.Jump,
  z: Btn.Jump,
  s: Btn.Down,
  arrowdown: Btn.Down,
  ' ': Btn.Cast,
  spacebar: Btn.Cast,
  v: Btn.Cast,
  x: Btn.Cast,
  f: Btn.Ranged,
  l: Btn.Ranged,
  e: Btn.Activate,
};

function bindingFor(e: KeyboardEvent): InputFrame | undefined {
  return BINDINGS[e.code] ?? KEY_FALLBACK[e.key.toLowerCase()];
}

/** Stable identity for the held-key set, so keyup always cancels its keydown. */
function keyId(e: KeyboardEvent): string {
  return e.code !== '' ? e.code : `key:${e.key.toLowerCase()}`;
}

export class KeyboardSource {
  private readonly held = new Set<string>();
  /**
   * Buttons pressed since the last sample, even if already released.
   *
   * Without this, a tap that begins and ends between two samples is lost
   * entirely: `held` is empty by the time the loop looks. That is invisible at a
   * steady 60Hz but real whenever frames are long — a background tab, a slow
   * device, a hitch during loading — and it eats exactly the quick cast taps the
   * game is built around. Latching guarantees every press is seen for at least
   * one simulation frame.
   */
  private latched: InputFrame = 0;
  private attached = false;

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    const bit = bindingFor(e);
    if (bit !== undefined) {
      e.preventDefault();
      if (!e.repeat) this.latched |= bit;
    }
    this.held.add(keyId(e));
  };
  private readonly onKeyUp = (e: KeyboardEvent): void => {
    this.held.delete(keyId(e));
  };
  private readonly onBlur = (): void => {
    this.held.clear();
    this.latched = 0;
  };

  attach(target: Window = window): void {
    if (this.attached) return;
    this.attached = true;
    target.addEventListener('keydown', this.onKeyDown);
    target.addEventListener('keyup', this.onKeyUp);
    target.addEventListener('blur', this.onBlur);
  }

  detach(target: Window = window): void {
    if (!this.attached) return;
    this.attached = false;
    target.removeEventListener('keydown', this.onKeyDown);
    target.removeEventListener('keyup', this.onKeyUp);
    target.removeEventListener('blur', this.onBlur);
    this.held.clear();
    this.latched = 0;
  }

  /** Consumes and returns this frame's input. Calling it twice in one frame is
   *  a bug: the second call loses any latched presses. */
  sample(): InputFrame {
    let b = this.latched;
    this.latched = 0;
    for (const id of this.held) {
      b |= id.startsWith('key:') ? (KEY_FALLBACK[id.slice(4)] ?? 0) : (BINDINGS[id] ?? 0);
    }
    return b;
  }

  /** Accepts a `code` (e.g. "KeyR"); also matches the `key:` fallback id. */
  isHeld(code: string): boolean {
    if (this.held.has(code)) return true;
    const logical = code.startsWith('Key') ? code.slice(3).toLowerCase() : code.toLowerCase();
    return this.held.has(`key:${logical}`);
  }
}
